import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateRangeFilter, { filterAppointmentsByDate } from "@/components/DateRangeFilter";
import AssignStaffPopover from "@/components/AssignStaffPopover";
import { toast } from "sonner";
import { Clock3 } from "lucide-react";

const STATUS = {
  confirmed: { text: "Confirmada", cls: "bg-secondary text-secondary-foreground" },
  completed: { text: "Completada", cls: "bg-slate-200 text-slate-700" },
  cancelled: { text: "Cancelada", cls: "bg-red-50 text-red-700" },
};

export default function AdminAppointments() {
  const [appts, setAppts] = useState([]);
  const [pending, setPending] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("next5");

  async function load() {
    const [a, p] = await Promise.all([
      api.get("/appointments"),
      api.get("/payments/pending").catch(() => ({ data: [] })),
    ]);
    setAppts(a.data);
    setPending(p.data || []);
  }
  useEffect(() => { load(); }, []);

  const dateFiltered = filterAppointmentsByDate(appts, dateFilter);
  const filtered = filter === "all" ? dateFiltered : dateFiltered.filter((a) => a.status === filter);

  async function changeStatus(id, status) {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch { toast.error("Error"); }
  }

  async function remove(id) {
    if (!confirm("¿Eliminar cita?")) return;
    await api.delete(`/appointments/${id}`);
    toast.success("Eliminada");
    load();
  }

  async function confirmPending(sessionId) {
    if (!confirm("Confirmar manualmente este pago como recibido?")) return;
    try {
      await api.post(`/payments/checkout/force-confirm/${sessionId}`);
      toast.success("Cita confirmada manualmente");
      load();
    } catch { toast.error("Error al confirmar"); }
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Citas</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Calendario</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} testidPrefix="admin-appt-range" />
          <div className="w-44">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="appointments-filter-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="confirmed">Confirmadas</SelectItem>
                <SelectItem value="completed">Completadas</SelectItem>
                <SelectItem value="cancelled">Canceladas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {pending.length > 0 && (
        <Card className="border-[hsl(var(--accent))]/40 bg-secondary/50 mb-6" data-testid="pending-bookings-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock3 className="h-4 w-4" style={{ color: "#1E3A8A" }} />
              <p className="overline" style={{ color: "#1E3A8A" }}>Pagos pendientes de confirmar ({pending.length})</p>
            </div>
            <p className="text-xs text-slate-600 mb-4">Reservas que iniciaron pago pero aún no se finalizaron. Si verificas el pago en Stripe, puedes confirmarlas manualmente.</p>
            <div className="space-y-2">
              {pending.map((p) => (
                <div key={p.session_id} className="flex items-center justify-between bg-white rounded-md border border-slate-200 px-4 py-3 text-sm" data-testid={`pending-row-${p.session_id}`}>
                  <div>
                    <p className="font-medium">{p.client_name} · {p.service_name}</p>
                    <p className="text-xs text-slate-500">{p.date} {p.time} · Depósito ${p.deposit_amount.toFixed(2)} · {p.client_email}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => confirmPending(p.session_id)} data-testid={`confirm-pending-${p.session_id}`}>Confirmar pago</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table data-testid="appointments-table">
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Hora</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Servicio</TableHead>
                <TableHead>Profesional</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">Sin citas</TableCell></TableRow>
              ) : filtered.map((a) => (
                <TableRow key={a.id} data-testid={`appt-row-${a.id}`}>
                  <TableCell className="font-medium">{a.date}</TableCell>
                  <TableCell>{a.time}</TableCell>
                  <TableCell>
                    <div className="font-medium">{a.client_name}</div>
                    <div className="text-xs text-slate-500">{a.client_email}</div>
                  </TableCell>
                  <TableCell>
                    <div>{a.service_name}</div>
                    <div className="text-xs text-slate-500">${a.service_price.toFixed(2)}</div>
                    {a.payment_status === "paid" && (
                      <div className="text-[10px] mt-1 uppercase tracking-wider font-semibold inline-flex items-center gap-1" style={{ color: "#1E3A8A" }} data-testid={`appt-paid-${a.id}`}>
                        ● Depósito ${(a.deposit_amount || 0).toFixed(2)} pagado
                      </div>
                    )}
                  </TableCell>
                  <TableCell data-testid={`appt-staff-${a.id}`} className="min-w-[180px]">
                    <AssignStaffPopover
                      appointmentId={a.id}
                      currentName={a.staff_name}
                      onAssigned={load}
                    />
                  </TableCell>
                  <TableCell><Badge className={STATUS[a.status]?.cls}>{STATUS[a.status]?.text}</Badge></TableCell>
                  <TableCell className="text-right space-x-2">
                    {a.status === "confirmed" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => changeStatus(a.id, "completed")} data-testid={`appt-complete-${a.id}`}>Completar</Button>
                        <Button size="sm" variant="ghost" onClick={() => changeStatus(a.id, "cancelled")} data-testid={`appt-cancel-${a.id}`}>Cancelar</Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(a.id)} data-testid={`appt-delete-${a.id}`}>Eliminar</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
